import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Database, Users as UsersIcon, Link2, LogOut, Sun, Moon,
    KeyRound, ShieldCheck, ChevronRight, ArrowRightLeft, Server, Sliders,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { NAV } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

function SidebarLink({ to, icon: Icon, label, testId, end }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
                [
                    'group flex items-center gap-3 px-3 py-2 rounded-sm text-sm border border-transparent',
                    'row-hover',
                    isActive
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                ].join(' ')
            }
        >
            <Icon size={16} strokeWidth={1.75} />
            <span className="font-medium tracking-tight">{label}</span>
        </NavLink>
    );
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();
    const isAdmin = user && user.role === 'admin';

    const initials = (user?.name || user?.email || '?')
        .split(/\s+|@/)[0]
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="flex min-h-screen">
                {/* Sidebar */}
                <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-card">
                    <div className="px-5 py-5 border-b border-border">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 grid place-items-center rounded-sm bg-primary text-primary-foreground font-display font-black">
                                ⌘
                            </div>
                            <div>
                                <div className="font-display font-black tracking-tight text-[15px] leading-none">
                                    SPACES PANEL
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav className="flex-1 p-3 space-y-1">
                        <SidebarLink
                            to="/"
                            icon={LayoutDashboard}
                            label="Dashboard"
                            testId={NAV.dashboardLink}
                            end
                        />
                        <SidebarLink
                            to="/spaces"
                            icon={Database}
                            label={isAdmin ? 'Spaces' : 'My Spaces'}
                            testId={NAV.spacesLink}
                        />
                        <SidebarLink
                            to="/droplets"
                            icon={Server}
                            label={isAdmin ? 'Droplets' : 'My Droplets'}
                            testId="nav-droplets-link"
                        />
                        {isAdmin && (
                            <>
                                <SidebarLink
                                    to="/users"
                                    icon={UsersIcon}
                                    label="Users"
                                    testId={NAV.usersLink}
                                />
                                <SidebarLink
                                    to="/assignments"
                                    icon={Link2}
                                    label="Space Assignments"
                                    testId={NAV.assignmentsLink}
                                />
                                <SidebarLink
                                    to="/droplet-assignments"
                                    icon={Sliders}
                                    label="Droplet Assignments"
                                    testId="nav-droplet-assignments-link"
                                />
                                <SidebarLink
                                    to="/jobs"
                                    icon={ArrowRightLeft}
                                    label="Copy Jobs"
                                    testId="nav-jobs-link"
                                />
                            </>
                        )}
                    </nav>
                    <div className="p-3 border-t border-border">
                        <div className="flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2">
                            <ShieldCheck size={14} className="text-primary" />
                            <span className="text-xs text-muted-foreground">
                                {isAdmin ? 'Admin session' : 'Read-only mode'}
                            </span>
                        </div>
                    </div>
                </aside>

                {/* Main */}
                <div className="flex-1 flex flex-col min-w-0">
                    <header className="h-14 border-b border-border bg-card/60 backdrop-blur-md sticky top-0 z-30">
                        <div className="h-full px-4 sm:px-6 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="md:hidden font-display font-black">SPACES</div>
                                <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>panel</span>
                                    <ChevronRight size={12} />
                                    <span className="text-foreground">{user?.role}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={toggle}
                                    data-testid={NAV.themeToggle}
                                    aria-label="Toggle theme"
                                    className="rounded-sm"
                                >
                                    {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            data-testid={NAV.userMenu}
                                            className="flex items-center gap-2 rounded-sm border border-border px-2 py-1.5 hover:bg-muted row-hover"
                                        >
                                            <div className="h-6 w-6 grid place-items-center rounded-sm bg-primary/15 text-primary text-xs font-bold">
                                                {initials}
                                            </div>
                                            <span className="hidden sm:block text-sm max-w-[160px] truncate">
                                                {user?.email}
                                            </span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-56 bg-popover border-border"
                                    >
                                        <DropdownMenuLabel className="text-xs">
                                            <div className="text-muted-foreground text-xs">
                                                Signed in
                                            </div>
                                            <div className="truncate">{user?.email}</div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => nav('/change-password')}
                                            data-testid={NAV.changePasswordButton}
                                        >
                                            <KeyRound size={14} className="mr-2" /> Change password
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={async () => {
                                                await logout();
                                                nav('/login', { replace: true });
                                            }}
                                            data-testid={NAV.logoutButton}
                                        >
                                            <LogOut size={14} className="mr-2" /> Logout
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </header>
                    <main className="flex-1 min-w-0">
                        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-8 py-8">
                            <Outlet />
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
